// src/DataTable_AgGrid.js
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ReactDOM from "react-dom";
import { AgGridReact } from 'ag-grid-react';
import { 
  ModuleRegistry,
  ClientSideRowModelModule,
  PinnedRowModule,
  QuickFilterModule,
  RowStyleModule,
  PaginationModule,

  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  LargeTextEditorModule,
  SelectEditorModule,
  CustomEditorModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DataTable_AgGrid.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from './axios';

// Registreer de modules (belangrijk voor AG Grid v33+)
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  PinnedRowModule,
  QuickFilterModule,
  RowStyleModule,
  PaginationModule,

  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  LargeTextEditorModule,
  SelectEditorModule,
  CustomEditorModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule
]);

// MemoCellRenderer (zoals hierboven)
const MemoCellRenderer = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [text, setText] = useState(props.value || "");

  const closeModal = () => setShowModal(false);
  
  const saveChanges = () => {
    props.node.setDataValue(props.column.colId, text);
    closeModal();
    if (props.onMemoSave) {
      props.onMemoSave(props.node.data);
    }
  };

  const openModal = () => {
    setShowModal(true);
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {text.length > 30 ? text.substring(0, 30) + "..." : text}
        </span>
        <button onClick={openModal} style={{ border: "none", background: "transparent", cursor: "pointer" }}>
          ⋮
        </button>
      </div>
      {showModal &&
        ReactDOM.createPortal(
          <div className="modal-overlay">
            <div className="modal">
              <h3>Edit Memo</h3>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows="5"
                style={{ width: "100%" }}
              />
              <div className='modal-buttons'>
                <button onClick={closeModal}>Cancel</button>
                <button onClick={saveChanges} style={{ marginLeft: "10px" }}>Save</button>
              </div>
            </div>
          </div>,
          document.body
        )
      }
    </>
  );
};

function DataTable_AgGrid({ initialData, columns = [], isEditable = false, config = {}, onOpenForm, moduleId, recordId }) {
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);

  const [rowData, setRowData] = useState(initialData || []);
  const originalRowRef = useRef(null);

  const [quickFilterText, setQuickFilterText] = useState('');
  const gridContainerRef = useRef(null);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showPopupMessage, setShowPopupMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const navigate = useNavigate();

  const { user, companyId, token, logout } = useAuth();
  

  const handleInviteCustomer = async (userData) => {
    try {
      // Axios-instance stuurt automatisch de Authorization-header
      await api.post('/api/send-invite-cp', {
        email:                 userData.EMAIL,
        invitedToCompanyId:    companyId,
        invitedByUserId:       user.id
      });

      // Succes-feedback
      setMessage('Invitation sent successfully');
      setShowPopupMessage(true);
      setTimeout(() => setShowPopupMessage(false), 3000);

    } catch (err) {
      // 401 → sessie verlopen
      if (err.response?.status === 401) {
        await logout();
        setError('Session expired');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        navigate(`/login/${companyId}`);
        return;
      }

      // Andere fouten
      const msg = err.response?.data?.error || err.message || 'Something went wrong';
      console.error('Error sending invitation:', msg);
      setError('Error sending invitation: ' + msg);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  const handleAddCustomer = async (userData) => {
    try {
      await api.post('/api/add-user-cp', {
        email:               userData.EMAIL,
        invitedToCompanyId:  companyId,
        invitedByUserId:     user.id
      });
      setMessage('Customer added successfully');
      setShowPopupMessage(true);
      setTimeout(() => setShowPopupMessage(false), 3000);
    } catch (err) {
      if (err.response?.status === 401) {
        await logout();
        setError('Session expired');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        navigate(`/login/${companyId}`);
        return;
      }
      const msg = err.response?.data?.error || err.message;
      setError('Error adding customer: ' + msg);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  const handleInviteEmployee = async (userData) => {
    try {
      // Axios-instance voegt automatisch Authorization-header toe
      await api.post('/api/send-invite-ep', {
        email:                 userData.E_EMAIL,
        invitedToCompanyId:    companyId,
        invitedByUserId:       user.id
      });

      // Succes-feedback
      setMessage('Invitation sent successfully');
      setShowPopupMessage(true);
      setTimeout(() => setShowPopupMessage(false), 3000);

    } catch (err) {
      // 401 → sessie verlopen
      if (err.response?.status === 401) {
        await logout();
        setError('Session expired');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        navigate(`/login/${companyId}`);
        return;
      }

      // Andere fouten
      const msg = err.response?.data?.error || err.message || 'Something went wrong';
      console.error('Error sending invitation:', msg);
      setError('Error sending invitation: ' + msg);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  const handleAddEmployee = async (userData) => {
    try {
      await api.post('/api/add-user-ep', {
        email:               userData.EMAIL,
        invitedToCompanyId:  companyId,
        invitedByUserId:     user.id
      });
      setMessage('Employee added successfully');
      setShowPopupMessage(true);
      setTimeout(() => setShowPopupMessage(false), 3000);
    } catch (err) {
      if (err.response?.status === 401) {
        await logout();
        setError('Session expired');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        navigate(`/login/${companyId}`);
        return;
      }
      const msg = err.response?.data?.error || err.message;
      setError('Error adding employee: ' + msg);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  const handleInviteSupplier = async (userData) => {
    try {
      // Axios-instance stuurt automatisch de Authorization-header
      await api.post('/api/send-invite-sp', {
        email:                 userData.EMAIL,
        invitedToCompanyId:    companyId,
        invitedByUserId:       user.id
      });

      // Succes-feedback
      setMessage('Invitation sent successfully');
      setShowPopupMessage(true);
      setTimeout(() => setShowPopupMessage(false), 3000);

    } catch (err) {
      // 401 → sessie verlopen
      if (err.response?.status === 401) {
        await logout();
        setError('Session expired');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        navigate(`/login/${companyId}`);
        return;
      }

      // Andere fouten
      const msg = err.response?.data?.error || err.message || 'Something went wrong';
      console.error('Error sending invitation:', msg);
      setError('Error sending invitation: ' + msg);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  const handleAddSupplier = async (userData) => {
    try {
      await api.post('/api/add-user-sp', {
        email:               userData.EMAIL,
        invitedToCompanyId:  companyId,
        invitedByUserId:     user.id
      });
      setMessage('Supplier added successfully');
      setShowPopupMessage(true);
      setTimeout(() => setShowPopupMessage(false), 3000);
    } catch (err) {
      if (err.response?.status === 401) {
        await logout();
        setError('Session expired');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        navigate(`/login/${companyId}`);
        return;
      }
      const msg = err.response?.data?.error || err.message;
      setError('Error adding supplier: ' + msg);
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };


/*
 * Haalt een document op als Blob via de API.
*/
async function fetchDocument(documentId) {
  try {
    // api.post stuurt automatisch de Authorization-header mee (instelling in axios-interceptor)
    const response = await api.post(
      '/api/CPUser_downloaddocument',
      {
        companyId:   companyId,
        email:       user.email,
        documentId:  documentId
      },
      {
        // Zorg dat axios de response als Blob teruggeeft
        responseType: 'blob'
      }
    );

    // Succes: return de Blob
    return response.data;

  } catch (err) {
    // 401 Unauthorized → sessie verlopen
    if (err.response?.status === 401) {
      await logout();
      alert('Session expired, please login again');
      return null;
    }

    // Algemene foutafhandeling
    console.error('Error fetching document:', err);
    alert('Error downloading document: file not found or access denied');
    return null;
  }
}

  // Definieer nu de DownloadDocument-functie en koppel deze aan het window-object:
  window.DownloadDocument = async function(rowData) {
    setIsDownloading(true); // 🔄 Start loading
  
    try {
      const documentId = rowData.FK_DOCUMENT;
      const blob = await fetchDocument(documentId);
  
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename = `${rowData.NAME}${rowData.EXTENSION}`;
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setMessage('Document downloaded succesfully');
        setShowPopupMessage(true);
        setTimeout(() => setShowPopupMessage(false), 3000); // Hide the popup after 3 seconds 
      }
    } catch (error) {
      console.error("Error downloading document: ", error);

      setError('Error downloading document: file not found or access denied');
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000); // Hide the popup after 3 seconds 
    } finally {
      setIsDownloading(false); // ✅ Stop loading regardless of success/failure
    }
  };
  
  function stripRtf(rtf) {
    if (!rtf) return '';
    return rtf
      .replace(/\\par[d]?/g, '\n')
      .replace(/\\'[0-9a-fA-F]{2}/g, '')                   // hex-encoded chars
      .replace(/\\[a-z]+\d* ?/gi, '')                      // control words
      .replace(/{\\\*\\[^{}]+}|[{}]/g, '')                 // groups & metadata
      .replace(/[\r\n]{2,}/g, '\n')                        // multiple line breaks
      .replace(/[;]+/g, ' ')                               // semicolons to space
      .replace(/(Calibri|Times New Roman|Segoe UI)/gi, '') // fonts
      .replace(/\s+/g, ' ')                                // collapse whitespace
      .trim();
  }
  
  
  // Update rowData als initialData verandert
  useEffect(() => {
    setRowData(initialData);
  }, [initialData]);

  useEffect(() => {
    window.handleInviteEmployee = handleInviteEmployee;
  }, [handleInviteEmployee]);
  
  useEffect(() => {
    window.handleAddEmployee = handleAddEmployee;
  }, [handleAddEmployee]);

  useEffect(() => {
    window.handleInviteCustomer = handleInviteCustomer;
  }, [handleInviteCustomer]);

  useEffect(() => {
    window.handleAddCustomer = handleAddCustomer;
  }, [handleAddCustomer]);

  useEffect(() => {
    window.handleInviteSupplier = handleInviteSupplier;
  }, [handleInviteSupplier]);

  useEffect(() => {
    window.handleAddSupplier = handleAddSupplier;
  }, [handleAddSupplier]);

  // Bouw de columnDefs op basis van 'columns' of gebruik defaults
  const columnDefs = useMemo(() => {
    if (columns && columns.length > 0) {
      return columns.map((col) => {
        const agColDef = {
          field: col.field,
          headerName: col.headerName,
          hide: col.hide,
          editable: col.editable && isEditable,
          tooltipField: col.field, // Tooltip toont altijd de volledige waarde
          headerTooltip: col.headerName, // Tooltip bij de kolomheader       
          cellStyle: (params) => {
            if (params.colDef.editable) {
              return { backgroundColor: "#ffffcc", border: "1px solid #ff9900" };
            }
            return null;
          },
          cellRendererFramework: (params) => <span>{params.value}</span>, // Lege renderer
        };
        
        // Als de kolom een case-mapping heeft, voeg een cellRenderer toe
        if (col.case) {
          agColDef.cellRenderer = (params) => {
            const value = params.value;
            return <span>{col.case[value] || value}</span>; // Toon gemapte waarde of fallback naar originele waarde
          };
        }

        if (col.defaultWidth) {
          agColDef.width = col.defaultWidth;
        }        
        
        if (col.cellClass) {
          agColDef.cellClass = col.cellClass;
        }
        if (col.valueGetter) {
          agColDef.valueGetter = (params) => {
            if (params.node.rowPinned) return null;
            const data = params.data || {};
            try {
              // Sanitize de valueGetter: vervang elke data.X door (data.X || '')
              const safeGetter = col.valueGetter.replace(/data\.(\w+)/g, '(data.$1 || \'\')');
              const fn = new Function('data', `return ${safeGetter};`);
              const value = fn(data);
              return value;
            } catch (e) {
              console.error("Error evaluating valueGetter:", e);
              return '';
            }
          };
        }
                           
        if (col.type === 'integer') {
          agColDef.filter = 'agNumberColumnFilter';
        } else {
          agColDef.filter = 'agTextColumnFilter';
        }
        if (col.type === 'decimal') {
          // Gebruik een number filter voor decimalen
          agColDef.filter = 'agNumberColumnFilter';
          // Als er een style is opgegeven, formatteren we op basis daarvan
          // Hier gaan we er voor het voorbeeld van uit dat "€ 0.000,00" betekent dat we 
          // de Euro currency formatteren met Nederlandse notatie.
          if (col.style && col.style.includes('€')) {
            agColDef.valueFormatter = (params) => {
              if (params.value == null) return '';
              return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(params.value);
            };
          } else {
            // Anders formatteren we de decimal met twee decimalen
            agColDef.valueFormatter = (params) => {
              if (params.value == null) return '';
              return Number(params.value).toFixed(2);
            };
          }
        }        
        if (agColDef.editable && col.type === 'string') {
          agColDef.cellEditor = 'agTextCellEditor';
        }
        if (agColDef.editable && col.type === 'integer') {
          agColDef.cellEditor = "agNumberCellEditor";
        }
        if (col.field === "SYMBOL") {
          agColDef.cellRenderer = (params) => <span className="symbol-font-class">{params.value}</span>;
        }
        if (col.editable && col.type !== 'date') {
          agColDef.cellRenderer = (params) => (
            <div style={{ backgroundColor: "#c7c705", /* … */ }} className="editable-cell">
              <span>{params.value ?? ""}</span>
            </div>
          );
        }       

        if (col.type === 'date') {
          // 1) Datum-filter
          agColDef.filter = 'agDateColumnFilter';

          // 2) Formatter voor schermweergave (dd-mm-yyyy)
          agColDef.valueFormatter = (params) => {
            if (!params.value) return '';
            const d  = new Date(params.value);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return col.style === 'dd-mm-yyyy'
              ? `${dd}-${mm}-${yyyy}`
              : d.toLocaleDateString();
          };

          // 3) Comparator voor correcte datum-sortering
          agColDef.comparator = (vA, vB) => {
            if (!vA && !vB) return 0;
            if (!vA) return -1;
            if (!vB) return 1;
            return new Date(vA) - new Date(vB);
          };

          // style zodat de input in het midden valt:
          agColDef.cellStyle = {
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            padding:        '0'
          };

          // 4) DatePicker + parser + custom renderer met styling
          if (agColDef.editable) {
            // gebruik AG-Grid’s datum-editor
            agColDef.cellEditor      = 'agDateCellEditor';
            agColDef.cellEditorPopup = false;

            // parser: converteer input naar ISO zonder shift-issues
            agColDef.valueParser = (params) => {
              const raw = params.newValue;
              let out;

              // AG-Grid kan soms een Date-object geven
              if (raw instanceof Date && !isNaN(raw)) {
                // haal lokale offset eraf
                const tzMs    = raw.getTimezoneOffset() * 60_000;
                const shifted = new Date(raw.getTime() - tzMs);
                out = shifted.toISOString().slice(0, 19); // "YYYY-MM-DDTHH:mm:ss"
              }
              // of parse “dd-mm-yyyy” string
              else if (typeof raw === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(raw)) {
                const [dd, mm, yyyy] = raw.split('-');
                out = `${yyyy}-${mm}-${dd}T00:00:00`;
              } else {
                out = null;
              }

              console.log('📤 [date valueParser]', { raw, out });
              return out;
            };

            // custom cellRenderer om achtergrond te stylen
            agColDef.cellRenderer = (params) => {
              // genereer hetzelfde display-formaat als valueFormatter
              let text = '';
              if (params.value) {
                const d  = new Date(params.value);
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                text = col.style === 'dd-mm-yyyy'
                  ? `${dd}-${mm}-${yyyy}`
                  : d.toLocaleDateString();
              }
              return (
                <span
                  style={{
                    display:         'block',
                    width:           '100%',
                    height:          '100%',
                    backgroundColor: '#c7c705',
                    border:          '1px solid #ff9900',
                    boxSizing:       'border-box',
                    padding:         '0 4px',
                  }}
                >
                  {text}
                </span>
              );
            };
          }
        }



        if (col.type === 'thumb_image') {
          agColDef.headerName = col.headerName || 'Image';
          agColDef.field = col.field;
        
          agColDef.cellRenderer = (params) => {
            const base64 = params.value;
            if (!base64) {
              return <span style={{ color: 'gray', fontStyle: 'italic' }}></span>;
            }
            return (
              <img
                src={`data:image/jpeg;base64,${base64}`}
                alt="Thumb"
                style={{
                  height: '40px',
                  width: 'auto',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            );
          };
        
          agColDef.width = 80;
          agColDef.sortable = true;
          agColDef.filter = false;
        }        
        
        if (col.type === 'rtf_memo') {
          agColDef.cellRenderer = (params) => {
            const plainText = stripRtf(params.value);
            return (
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '13px',
                  color: '#333',
                  maxHeight: '150px',
                  overflowY: 'auto',
                }}
              >
                {plainText}
              </div>
            );
          };
        }
        
        
        // Als het type memo is, gebruik MemoCellRenderer en geef een onMemoSave callback mee
        if (col.type === "memo") {
          agColDef.cellRenderer = (params) => (
            <MemoCellRenderer
              {...params}
              onMemoSave={async (updatedRow) => {
                // Bepaal de primaire sleutelnaam uit config of kolomconfiguratie
                const primaryKeyField = config.primaryKey || (columns.find(col => col.primaryKey)?.field) || "id";
                // Filter alleen de editable velden
                const editableFields = columns.filter(col => col.editable).map(col => col.field);
                const filteredData = {};
                editableFields.forEach(field => {
                  if (updatedRow.hasOwnProperty(field)) {
                    filteredData[field] = updatedRow[field] === "" ? "" : updatedRow[field];  
                  }
                });
                // Voeg de primaire sleutel toe
                filteredData[primaryKeyField] = updatedRow[primaryKeyField];

                // Bouw de payload
                const payload = {
                  companyId:   companyId,
                  email:       user.email,
                  tableName:   config.tablename,
                  primaryKey:  primaryKeyField,
                  data:        filteredData
                };

                try {
                  setIsLoading(true);

                  // Axios POST: api-instance voegt Authorization-header automatisch mee
                  const { data: result } = await api.post(
                    '/api/iAGateway_update_query',
                    payload
                  );

                  // Succes
                  console.log('Memo update successful:', result);
                  setMessage('Memo updated successfully');
                  setShowPopupMessage(true);
                  setTimeout(() => setShowPopupMessage(false), 3000);

                } catch (err) {
                  console.error('Error updating memo:', err);
                  setError(err.response?.data?.error || err.message);
                  setShowErrorPopup(true);
                  setTimeout(() => setShowErrorPopup(false), 3000);

                } finally {
                  setIsLoading(false);
                }
              }}
            />
          );
        }

        // Voor URL-kolommen: gebruik de URL cellRenderer
        if (col.type === 'url' && col.url_template) {
          agColDef.cellRenderer = (params) => {
            if (params.node.rowPinned) {
              return null;
            }
            const rowVals = params.data;
            const text = params.value;
            if (!text) return null;
            let link = col.url_template;
            if (rowVals.ORDERNUMBER) {
              link = link.replace('{ORDERNUMBER}', rowVals.ORDERNUMBER);
            }
            if (rowVals.JOBORDERNUMBER) {
              link = link.replace('{JOBORDERNUMBER}', rowVals.JOBORDERNUMBER);
            }
            if (rowVals.PK_U_KEURINGSRAPPORT_BON) {
              link = link.replace('{PK_U_KEURINGSRAPPORT_BON}', rowVals.PK_U_KEURINGSRAPPORT_BON);
            }
            link = link.replace('{value}', text);
            return <a href={link} style={{ color: 'blue' }}>{text}</a>;
          };
        }

        if (col.type === 'button') {
          agColDef.cellRenderer = (params) => {
            if (params.node.rowPinned) {
              return null;
            }
            return (
              <button
                onClick={async () => {
                  // 1) Bepaal recordId
                  const row = params.data;
                  let subtable_recordId = null;
                  if ('id' in row) {
                    subtable_recordId = row.id;
                  } else {
                    const pkField = Object.keys(row).find(key => key.startsWith('PK_'));
                    if (pkField) {
                      subtable_recordId = row[pkField];
                    } else {
                      const fkField = Object.keys(row).find(key => key.startsWith('FK_'));
                      if (fkField) {
                        subtable_recordId = row[fkField];
                      }
                    }
                  }

                  // 2) Log de button-klik
                  try {
                    await api.post('/api/log_activity', {
                      companyId,
                      moduleId,
                      recordId,
                      subtable_recordId,
                      activityDesc: `Button "${col.buttontext}"`
                    });
                  } catch (err) {
                    console.error('Activity logging failed:', err);
                  }

                  // 3) Voer de oorspronkelijke actie uit
                  if (col.function && typeof window[col.function] === 'function') {
                    window[col.function](params.data);
                  } else {
                    console.error(`Function ${col.function} not defined.`);
                  }
                }}
                className="centered-button"
              >
                {col.buttontext}
              </button>
            );
          };
          agColDef.cellClass = 'centered-cell';
        }
        
        if (col.type === 'openForm') {
          agColDef.cellRenderer = (params) => {
            if (params.node.rowPinned) {
              return null;
            }
            // Zorg dat 0 niet wordt overschreven door een lege string:
            const displayText = (params.value !== undefined && params.value !== null) ? params.value : '';
            // Als in de JSON definitie moduleId leeg is, gebruik dan de eigen moduleId (de prop), anders de opgegeven moduleId
            const targetModuleId = col.moduleId && col.moduleId.trim() !== '' ? col.moduleId : moduleId;
            // Voor recordId: als er een recordId-veld in de JSON is, gebruik die als de key om de waarde uit de rij op te halen,
            // anders gebruik je de primaryKey uit de config of een fallback (bijvoorbeeld 'id')
            const targetRecordId = col.recordId ? params.data[col.recordId] : (params.data[config.primaryKey] || params.data.id);
            return (
              <a
                href="#"
                style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  if (onOpenForm && typeof onOpenForm === 'function') {
                    onOpenForm({ recordId: targetRecordId, moduleId: targetModuleId });
                  } else {
                    console.error('onOpenForm callback not provided.');
                  }
                }}
              >
                <span style={{ textDecoration: 'none', marginRight: '5px', display: 'inline-block' }}>
                  🔍
                </span>
                <span>{displayText}</span>
              </a>
            );
          };
          agColDef.cellClass = 'centered-cell';
        }                                                    


        return agColDef;
      });
    }
  }, [columns, isEditable]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    //flex: 1,
    suppressNavigable: true, // Voorkomt focus op de cel en dus het pennetje
  }), []);

  const onGridReady = useCallback(params => {
    // sla de API’s op
    setGridApi(params.api);
  }, []);


  const handleQuickFilter = (e) => {
    const value = e.target.value || '';
    setQuickFilterText(value);
  };

  const onRowEditingStarted = useCallback(params => {
    // Maak een ongewijzigde kopie van de rij
    originalRowRef.current = { ...params.data };
  }, []);

  // Wanneer de rij-editing stopt, voer de update uit
  const onRowEditingStopped = useCallback(
    async (params) => {
      setIsLoading(true);
      const userEmail = user?.email;

      // 1) originele en gewijzigde rij
      const originalRow = originalRowRef.current || {};
      const updatedRow  = params.data;

      // 2) bepaal primary key en bewerkbare velden
      const primaryKeyField =
        config.primaryKey ||
        columns.find((col) => col.primaryKey)?.field ||
        'id';
      const editableFields = columns
        .filter((c) => c.editable)
        .map((c) => c.field);

      // 3) check op echte wijzigingen
      let changed = false;
      for (const f of editableFields) {
        let oldVal = originalRow[f];
        let newVal = updatedRow[f];

        // special case date?
        const colType = columns.find(c => c.field === f)?.type;
        if (colType === 'date') {
          const fmt = (v) => {
            if (!v) return '';
            const d = new Date(v);
            const dd = String(d.getDate()).padStart(2,'0');
            const mm = String(d.getMonth()+1).padStart(2,'0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          };
          oldVal = fmt(oldVal);
          newVal = fmt(newVal);
        }

        if (oldVal !== newVal) {
          changed = true;
          break;
        }
      }

      if (!changed) {
        // geen wijziging → stop
        setIsLoading(false);
        return;
      }

      // 4) lokaal updaten voor UX
      setRowData(old =>
        old.map(item =>
          item[primaryKeyField] === updatedRow[primaryKeyField]
            ? updatedRow
            : item
        )
      );

      // 5) bouw payload (inclusief datum-correctie)
      const filteredData = { [primaryKeyField]: updatedRow[primaryKeyField] };
      editableFields.forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(updatedRow, f)) {
          const val = updatedRow[f];
          if (val instanceof Date && !isNaN(val)) {
            const tzOffsetMs = val.getTimezoneOffset() * 60_000;
            const corrected = new Date(val.getTime() - tzOffsetMs);
            filteredData[f] = corrected.toISOString().slice(0, 19);
          } else {
            filteredData[f] = val;
          }
        }
      });

      const payload = {
        companyId,
        email: userEmail,
        tableName: config.tablename,
        primaryKey: primaryKeyField,
        data: filteredData,
      };

      // 6) API-call
      try {
        const { data: result } = await api.post(
          '/api/iAGateway_update_query',
          payload
        );
        console.log('Row updated successfully:', result);
        setMessage('Row updated successfully');
        setShowPopupMessage(true);
      } catch (err) {
        console.error('Error updating row:', err);
        if (err.response?.status === 401) {
          await logout();
          setError('Session expired, please login again');
          navigate(`/login/${companyId}`);
        } else {
          setError(err.message || 'Error updating row');
        }
        setShowErrorPopup(true);
      } finally {
        setIsLoading(false);
        setTimeout(() => setShowPopupMessage(false), 3000);
        setTimeout(() => setShowErrorPopup(false), 3000);
      }
    },
    [columns, config, user, logout, companyId, navigate]
  );


  const handleExportCSV = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({ fileName: 'export.csv' });
    }
  };

  const getRowClass = (params) => {
    return '';
  };

  // Voeg een effect toe om te stoppen met bewerken als er buiten de grid wordt geklikt
  useEffect(() => {
    function handleClickOutside(e) {
      if (gridContainerRef.current && !gridContainerRef.current.contains(e.target)) {
        // Gebruik optionele chaining voor veiligheid
        if (gridApi?.getEditingCells?.()?.length > 0) {
          gridApi.stopEditing();
        }
      }
    }
    document.addEventListener('click_outside_datatable', handleClickOutside);
    return () => {
      document.removeEventListener('click_outside_datatable', handleClickOutside);
    };
  }, [gridApi]);

  const gridOptions = {
    suppressCellFlash: true,
    singleClickEdit: true,
    suppressKeyboardEvent: (params) => params.editing,
    tooltipShowDelay: 0,
    tooltipHideDelay: 3000,
  };  
  
  useEffect(() => {
    if (gridApi) {
      gridApi.refreshCells({ force: true });
    }
  }, [gridApi]);

  useEffect(() => {
    if (rowData && rowData.length > 0) {
      const totalsRow = {};
      columns.forEach(col => {
        if (col.showTotal) {
          totalsRow[col.field] = rowData.reduce((acc, row) => acc + (Number(row[col.field]) || 0), 0);
        }
      });
      setPinnedBottomRowData([totalsRow]);
    } else {
      setPinnedBottomRowData([]);
    }
  }, [rowData, columns]);
  
  useEffect(() => {
    // Controleer of er kolommen met showTotal: true zijn
    const hasTotals = columns.some(col => col.showTotal);
    if (!hasTotals) {
      setPinnedBottomRowData([]);
      return;
    }
  
    if (rowData && rowData.length > 0) {
      const totalsRow = {};
      columns.forEach(col => {
        if (col.showTotal) {
          totalsRow[col.field] = rowData.reduce((acc, row) => acc + (Number(row[col.field]) || 0), 0);
        }
      });
      setPinnedBottomRowData([totalsRow]);
    } else {
      setPinnedBottomRowData([]);
    }
  }, [rowData, columns]);
  
 
  return (

      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{ marginBottom: '10px', textAlign: 'right' }}>
          <input
            type="text"
            value={quickFilterText}
            onChange={handleQuickFilter}
            placeholder="Search..."
            style={{ width: '200px', marginRight: '10px' }}
          />
          {/*<button onClick={handleExportCSV}>Export to CSV</button>*/}
        </div>
        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ flex: 1, height: '100%' }}>
          <AgGridReact
            onGridReady={onGridReady}
            rowData={rowData}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            defaultColDef={defaultColDef}
            onRowEditingStarted={onRowEditingStarted}
            onRowEditingStopped={onRowEditingStopped}
            quickFilterText={quickFilterText}
            getRowClass={getRowClass}
            animateRows={true}
            pagination={true}
            paginationPageSize={50}
            suppressMovableColumns={false}
            editType="fullRow"
            tooltipShowDelay={0} // Toon de tooltip direct
            tooltipHideDelay={3000} // Optioneel: Verberg tooltip na 3 seconden       
            pinnedBottomRowData={pinnedBottomRowData}  // Hier geef je de totalen mee 
          />
        </div>

        {showPopupMessage && <div className="popup-message">{message}</div>}
        {showErrorPopup && <div className="popup">{error}</div>}
        {isLoading && (
        <div className="loader-wrapper-new">
            <div className="loader-new"></div>
        </div>
        )}
        {isDownloading && (
        <div className="loader-wrapper-new">
            <div className="loader-new"></div>
        </div>
        )}
      </div>
  );
}


export default DataTable_AgGrid;
